import { Amenity } from '@domain/graphql.types';
import { minutesSinceMidnightInTZ } from './realTimeLocation';

// Average walking speed is approximately 80 meters per minute.
const AVERAGE_WALKING_SPEED_M_PER_MIN = 80;

export const walkingMinutes = (meters: number) => Math.max(1, Math.ceil(meters / AVERAGE_WALKING_SPEED_M_PER_MIN));

type Weekday = 'sunday' | 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday';
type OpeningRange = { open: string; close: string };

const weekdays: Weekday[] = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const weekdayInTZ = (now: Date, timeZone: string): Weekday => {
  const day = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(now).toLowerCase();
  return (weekdays as string[]).includes(day) ? (day as Weekday) : weekdays[now.getDay()];
};

const toMinutes = (time: string) => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + (m || 0);
};

/** Today's state of an amenity, in the station's timezone: times are minutes since midnight. */
export type OpeningState = { kind: 'open'; closesAt: number } | { kind: 'opensLater'; opensAt: number } | { kind: 'closed' } | { kind: 'unknown' };

export const getOpeningState = (openingHours: Amenity['openingHours'], timeZone = 'UTC', now = new Date()): OpeningState => {
  if (!openingHours || typeof openingHours !== 'object') return { kind: 'unknown' };

  const ranges: OpeningRange[] = openingHours[weekdayInTZ(now, timeZone)] ?? [];
  const nowMinutes = minutesSinceMidnightInTZ(now, timeZone);

  let opensAt: number | null = null;
  for (const range of ranges) {
    const start = toMinutes(range.open);
    const end = toMinutes(range.close);
    if (nowMinutes >= start && nowMinutes < end) return { kind: 'open', closesAt: end };
    if (nowMinutes < start && (opensAt === null || start < opensAt)) opensAt = start;
  }

  return opensAt === null ? { kind: 'closed' } : { kind: 'opensLater', opensAt };
};

/** A time of day in the user's language: "10 PM", "10:30 PM", "22 h"… */
export const formatTimeOfDay = (minutes: number, locale: string) => {
  const h = Math.floor(minutes / 60) % 24;
  const m = minutes % 60;
  return new Intl.DateTimeFormat(locale, { hour: 'numeric', minute: m ? '2-digit' : undefined, timeZone: 'UTC' }).format(Date.UTC(2000, 0, 1, h, m));
};

/** Amenities with a name, nearest first. */
export const sortAmenitiesByWalkingDistance = (amenities: readonly (Amenity | null)[] | null | undefined): Amenity[] =>
  (amenities ?? []).filter((a): a is Amenity => !!a?.name).sort((a, b) => a.distanceWalkingMeters - b.distanceWalkingMeters);
